import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  InputBase,
  Divider,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTabs } from '../../contexts/TabsContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';

import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import ConfirmationDialog from '../modals/ConfirmationDialog';
import { useTranslation } from 'react-i18next';

// Styled Components
const TabsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: 'max-content',
  alignItems: 'flex-end',
  height: '100%',
  paddingLeft: theme.spacing(1),
}));

const TabItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 16px',
  minWidth: '100px',
  textAlign: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderRadius: '8px 8px 0 0',
  margin: '0 2px',
  backgroundColor: active ? theme.palette.background.paper : 'rgba(0,0,0,0.04)',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  fontWeight: active ? 600 : 400,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease-in-out',
  // Using box-shadow for borders is more robust against transform glitches
  boxShadow: `inset 0 1px 0 0 ${theme.palette.divider}, inset 1px 0 0 0 ${theme.palette.divider}, inset -1px 0 0 0 ${theme.palette.divider}`,
  borderBottom: `1px solid ${active ? theme.palette.background.paper : theme.palette.divider}`,
  marginBottom: '-1px',
  '&:hover': {
    backgroundColor: active ? theme.palette.background.paper : 'rgba(0,0,0,0.04)',
    color: theme.palette.primary.main,
  },
}));

const InlineInput = styled(InputBase)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  padding: 0,
  margin: 0,
  width: '100%',
}));

const TabsControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(1),
}));

// SortableTab Component
const SortableTab = ({ active, isEditing, children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-id'],
    disabled: isEditing,
  });

  const style = {
    // By directly setting the transform and forcing the Y-axis to 0, we prevent any vertical movement.
    // Using translate3d can also help with performance by hinting the browser to use hardware acceleration.
    transform: transform ? `translate3d(${transform.x}px, 0, 0)` : undefined,
    transition,
    zIndex: isDragging ? 999 : active ? 1 : 'auto',
    position: 'relative',
  };

  // Remove active from props to avoid passing it to DOM
  const { 'data-id': dataId, ...domProps } = props;

  return (
    <TabItem 
      ref={setNodeRef} 
      style={style} 
      active={active} 
      data-id={dataId}
      {...domProps} 
      {...attributes} 
      {...(isEditing ? {} : listeners)} // Only apply listeners when not editing
    >
      {children}
    </TabItem>
  );
};

// Main Component
const TabsToolbar = () => {
  const { t } = useTranslation();
  const { tabs, activeTabId, setActiveTabId, createTab, duplicateTab, duplicateActiveTab, renameTab, deleteTab, reorderTabs, closeAllTabs } = useTabs();
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);
  const [menuTabId, setMenuTabId] = useState(null);
  const [allTabsMenu, setAllTabsMenu] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabName, setEditingTabName] = useState('');
  const tabsContainerRef = useRef(null);
  const menuTimeoutRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState({ left: false, right: false });

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require mouse to move 8px to start dragging
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const updateScrollButtons = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isAtStart = scrollLeft < 1;
    const isAtEnd = scrollWidth - clientWidth - scrollLeft < 1;
    setShowScrollButtons({ left: !isAtStart, right: !isAtEnd && scrollWidth > clientWidth });
  }, []);

  useLayoutEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(container);
    container.addEventListener('scroll', updateScrollButtons, { passive: true });
    updateScrollButtons(); // Initial check
    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', updateScrollButtons);
    };
  }, [tabs, updateScrollButtons]);

  const handleScroll = (direction) => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -container.clientWidth * 0.7 : container.clientWidth * 0.7;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (editingTabId) return; // Prevent reordering while editing
    if (active && over && active.id !== over.id && tabs) {
      const oldIndex = tabs.findIndex((tab) => tab.id === active.id);
      const newIndex = tabs.findIndex((tab) => tab.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new array with reordered tabs
        const newTabs = [...tabs];
        const [movedTab] = newTabs.splice(oldIndex, 1);
        newTabs.splice(newIndex, 0, movedTab);
        reorderTabs(newTabs);
      }
    }
  }, [tabs, reorderTabs, editingTabId]);

  const handleCreateTab = useCallback(() => {
    const newTabId = createTab({});
    setActiveTabId(newTabId);
  }, [createTab, setActiveTabId]);

  const handleContextMenu = (event, tabId) => {
    event.preventDefault();
    if (editingTabId) return; // Don't show context menu when editing
    setMenuTabId(tabId);
    setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    // Don't clear menuTabId here - it might be needed by menu item handlers
  };

  const handleStartRename = () => {
    const tabToRename = tabs.find((tab) => tab.id === menuTabId);
    if (tabToRename) {
      setEditingTabId(menuTabId);
      setEditingTabName(tabToRename.name);
      // Use setTimeout to ensure state updates are processed
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = setTimeout(() => {
        setMenuTabId(null);
      }, 0);
    }
    handleCloseContextMenu();
  };

  const handleConfirmRename = () => {
    if (editingTabId && editingTabName.trim()) {
      renameTab(editingTabId, editingTabName.trim());
    }
    setEditingTabId(null);
    setEditingTabName('');
  };

  const handleDuplicateTab = (tabId) => {
    if (tabId === activeTabId) {
      // If duplicating active tab, use enhanced function with current state
      duplicateActiveTab();
    } else {
      // If duplicating non-active tab, use regular function
      const newTabId = duplicateTab(tabId);
      setActiveTabId(newTabId);
    }
  };

  const handleDeleteTab = () => {
    if (menuTabId) {
      deleteTab(menuTabId);
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = setTimeout(() => {
        setMenuTabId(null);
      }, 0);
    }
    handleCloseContextMenu();
  };

  const handleOpenAllTabsMenu = (event) => {
    setAllTabsMenu(event.currentTarget);
  };

  const handleCloseAllTabsMenu = () => {
    setAllTabsMenu(null);
  };

  const handleSelectTabFromMenu = useCallback((tabId) => {
    setActiveTabId(tabId);
    handleCloseAllTabsMenu();
  }, [setActiveTabId, handleCloseAllTabsMenu]);

  const handleOpenConfirmDialog = () => {
    handleCloseAllTabsMenu();
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmCloseAll = () => {
    closeAllTabs();
    handleCloseConfirmDialog();
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', ml: 2 }}>
        {showScrollButtons.left && (
          <IconButton onClick={() => handleScroll('left')} size="small">
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', height: '38px' }}>
          <Box
            ref={tabsContainerRef}
            sx={(theme) => ({
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
        >
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToHorizontalAxis, restrictToParentElement]}>
            <SortableContext items={tabs?.map((t) => t.id) || []} strategy={horizontalListSortingStrategy}>
              <TabsContainer>
                {tabs?.map((tab) => (
                  <SortableTab
                    key={tab.id}
                    data-id={tab.id}
                    active={tab.id === activeTabId}
                    isEditing={editingTabId === tab.id}
                    onClick={() => !editingTabId && setActiveTabId(tab.id)}
                    onContextMenu={(e) => handleContextMenu(e, tab.id)}
                    onDoubleClick={() => {
                      setEditingTabId(tab.id);
                      setEditingTabName(tab.name);
                    }}
                  >
                    {editingTabId === tab.id ? (
                      <InlineInput
                        value={editingTabName}
                        onChange={(e) => setEditingTabName(e.target.value)}
                        onBlur={handleConfirmRename}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleConfirmRename();
                          if (e.key === 'Escape') setEditingTabId(null);
                        }}
                        inputRef={(input) => {
                          if (input) {
                            setTimeout(() => {
                              input.focus();
                              // Move cursor to end instead of selecting all
                              const length = input.value.length;
                              input.setSelectionRange(length, length);
                            }, 10);
                          }
                        }}
                      />
                    ) : (
                      tab.name
                    )}
                  </SortableTab>
                )) || []}
              </TabsContainer>
            </SortableContext>
          </DndContext>
        </Box>
        </Box> 
        {showScrollButtons.right && (
          <IconButton onClick={() => handleScroll('right')} size="small">
            <ChevronRightIcon />
          </IconButton>
        )}
        <TabsControls>
          <Tooltip title={`${t('tabs.newCalculation')} (${navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+;)`}>
            <IconButton onClick={handleCreateTab} size="small" sx={{ ml: 1 }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tabs.allCalculations')}>
            <IconButton
              onClick={handleOpenAllTabsMenu}
              size="small"
              aria-controls={Boolean(allTabsMenu) ? 'all-tabs-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(allTabsMenu) ? 'true' : undefined}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </TabsControls>
      </Box>

      {/* Context Menu */}
      <Menu open={contextMenu !== null} onClose={handleCloseContextMenu} anchorReference="anchorPosition" anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
        <MenuItem onClick={handleStartRename}>Rename</MenuItem>
        <MenuItem onClick={() => {
          handleDuplicateTab(menuTabId);
          if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
          menuTimeoutRef.current = setTimeout(() => {
            setMenuTabId(null);
          }, 0);
          handleCloseContextMenu();
        }}>Duplicate ({navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+')</MenuItem>
        <MenuItem onClick={handleDeleteTab} disabled={(tabs?.length || 0) <= 1}>Delete</MenuItem>
      </Menu>

      {/* All Tabs Menu */}
      <Menu id="all-tabs-menu" anchorEl={allTabsMenu} open={Boolean(allTabsMenu)} onClose={handleCloseAllTabsMenu} MenuListProps={{ 'aria-labelledby': 'all-tabs-button' }}>
        {tabs?.map((tab) => (
          <MenuItem key={tab.id} onClick={() => handleSelectTabFromMenu(tab.id)} selected={tab.id === activeTabId}>
            {tab.name}
          </MenuItem>
        )) || []}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleOpenConfirmDialog} disabled={tabs.length <= 1}>
          <Typography color="error">Close All Tabs</Typography>
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog for closing all tabs */}
      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmCloseAll}
        title="Close All Tabs?"
        message={`Are you sure you want to close all ${tabs.length} tabs? This action cannot be undone.`}
      />
    </>
  );
};

export default TabsToolbar;
